import { useOutletContext } from "react-router";
import { useState } from "react";

import {
  SCENARIO_MODEL_MODE,
  ScenarioModelModeType,
} from "../pages/BBE/constants";
import { Baseline, BBEOutletContext } from "../pages/BBE/types";

import { useScenarioNamesQuery } from "./useBbe";
import { useScenarioModelActions } from "./useScenarioModelActions";

type UseScenarioModelModalResults = {
  onClickConfirm: (input: string) => void;
  handleOpenModal: (mode: ScenarioModelModeType) => void;
  handleExited: () => void;
  handleCloseModal: () => void;
  scenarioModelModalDefaultValueInput: string;
  modalOpen: boolean;
  modalMode: ScenarioModelModeType | null;
  isSubmitting: boolean;
};

export function useScenarioModelModal(
  selectedModel: Baseline | null,
): UseScenarioModelModalResults {
  const { brokerId, selectedModelId } = useOutletContext<BBEOutletContext>();

  const [
    scenarioModelModalDefaultValueInput,
    setScenarioModelModalDefaultValueInput,
  ] = useState<string>("");
  const [modalMode, setModalMode] = useState<ScenarioModelModeType | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const scenarioNames = useScenarioNamesQuery(selectedModelId);

  const handleCloseModal = (): void => {
    setModalOpen(false);
  };

  const handleExited = (): void => {
    setModalMode(null);
  };

  const { renameModel, createModel, addScenario } = useScenarioModelActions(
    selectedModelId,
    brokerId,
    handleCloseModal,
  );

  const handleOpenModal = (mode: ScenarioModelModeType): void => {
    setModalMode(mode);
    setModalOpen(true);

    switch (mode) {
      case SCENARIO_MODEL_MODE.RenameModelName:
        setScenarioModelModalDefaultValueInput(selectedModel?.label ?? "");
        break;
      case SCENARIO_MODEL_MODE.Add: {
        const names = scenarioNames?.data ?? [];
        const takenNames = new Set(
          names.map((name) => name.trim().toLowerCase()),
        );
        const nextLetter =
          "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            .split("")
            .find(
              (letter) => !takenNames.has(`scenario ${letter.toLowerCase()}`),
            ) ?? `${names.length + 1}`;
        setScenarioModelModalDefaultValueInput(`Scenario ${nextLetter}`);
        break;
      }
      case SCENARIO_MODEL_MODE.Create:
      default:
        setScenarioModelModalDefaultValueInput("Snapshot");
        break;
    }
  };

  const onClickConfirm = (input: string): void => {
    if (!modalMode) return;

    switch (modalMode) {
      case SCENARIO_MODEL_MODE.RenameModelName:
        renameModel.mutate(input);
        break;
      case SCENARIO_MODEL_MODE.Create:
        createModel.mutate(input);
        break;
      case SCENARIO_MODEL_MODE.Add:
        addScenario.mutate({ name: input });
        break;
    }
  };

  return {
    onClickConfirm,
    handleOpenModal,
    handleExited,
    handleCloseModal,
    scenarioModelModalDefaultValueInput,
    modalOpen,
    modalMode,
    isSubmitting:
      addScenario.isPending || createModel.isPending || renameModel.isPending,
  };
}
